<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import SoilTypesTable from '@/components/soil-type/SoilTypesTable.vue'
import ActionBar from '@/components/common/ActionBar.vue'
import RevisionSelect from '@/components/revision/RevisionSelect.vue'
import { REVISION_KEY } from '@/components/constant'
import RevisionDialog from '@/components/revision/RevisionDialog.vue'
import DownloadDialog from '@/components/common/DownloadDialog.vue'
import ImportSoilTypesDialog from '@/components/soil-type/ImportSoilTypesDialog.vue'
import download from '@/composables/downloadComposable'

const route = useRoute()
const store = useStore()

const storeNamespace = ref('baseSoilType')
const importDialog = ref(false)
const downloadDialog = ref(false)
const exportDialog = ref(false)

const table = ref(null)

const revisionKey = computed(() => REVISION_KEY.SOIL_TYPE)
const baseItems = computed(() => store.state.baseSoilType.items)
const downloadLoading = computed(() => store.state.baseSoilType.downloadLoading)
const exportLoading = computed(() => store.state.baseSoilType.exportLoading)
const templateFileName = computed(() => store.state.baseSoilType.templateFileName)
const loading = computed(() => store.state.soilType.loading)
const items = computed(() => store.state.soilType.items)
const exportFileName = computed(() => store.getters['baseSoilType/getExportFileName'])
const revisionId = computed(() => parseInt(route.params.revisionId))
const filteredItems = computed(() => items.value.filter((item) => item.soil_type_revision === revisionId.value))

const add = () => store.dispatch('baseSoilType/add')
const fetchItemsAdd = (revisionId) => store.dispatch('soilType/fetchItemsAdd', revisionId)
const resetDirtyItems = () => store.dispatch('soilType/resetDirtyItems')
const downloadTemplate = () => store.dispatch('baseSoilType/downloadTemplate')
const exportData = () => store.dispatch('baseSoilType/exportData')

const save = async () => {
  await table.value.saveChanges()
}

const downloadFile = async () => {
  let response = await downloadTemplate()
  if (response) {
    download(response, templateFileName.value)
  }
  downloadDialog.value = false
}

const exportFile = async () => {
  let response = await exportData()
  if (response) {
    download(response, exportFileName.value)
  }
  exportDialog.value = false
}

const fetchAddSoilTypes = async () => {
  resetDirtyItems()
  if (!loading.value) {
    await fetchItemsAdd(revisionId.value)
  }
}
</script>

<template>
  <div>
    <ActionBar
      class="pr-3 align-center"
      download-text="template"
      export-text="data export"
      import-text="data import"
      show-add
      show-save
      show-import-right
      show-download-right
      show-export-right
      @add="add"
      @download="downloadDialog = true"
      @export="exportDialog = true"
      @import="importDialog = true"
      @save="save()"
    >
      <template v-slot:left>
        <div class="d-flex align-center">
          <v-card-title> Soil type </v-card-title>
          <RevisionSelect
            label="Revision"
            style="width: 250px"
            density="compact"
            store-namespace="soilTypeRevision"
            :parameter="revisionKey"
            @update="fetchAddSoilTypes()"
          ></RevisionSelect>
        </div>
        <v-card-subtitle> Please configure the soil types </v-card-subtitle>
      </template>
    </ActionBar>

    <SoilTypesTable ref="table" :base-items="baseItems" :items="filteredItems" class="pa-3" />
    <RevisionDialog store-namespace="soilTypeRevision" :parameter="revisionKey"></RevisionDialog>

    <ImportSoilTypesDialog
      :dialog="importDialog"
      :store-namespace="storeNamespace"
      @update:dialog="importDialog = $event"
    />
    <DownloadDialog
      :dialog="downloadDialog"
      :file-name="templateFileName"
      :loading="downloadLoading"
      :width="500"
      text="Download soil types template"
      @download="downloadFile()"
      @update:dialog="downloadDialog = $event"
    ></DownloadDialog>
    <DownloadDialog
      :dialog="exportDialog"
      :file-name="exportFileName"
      :loading="exportLoading"
      :width="500"
      text="Export soil types data"
      @download="exportFile()"
      @update:dialog="exportDialog = $event"
    ></DownloadDialog>
  </div>
</template>
